type QuietResult = 'quiet' | 'timeout' | 'aborted';

/**
 * Resolves once `isBusy` has been false for `quietMs` without a new change.
 * `subscribe` must call the listener whenever the busy state may have changed.
 */
export function waitUntilQuiet({
  isBusy,
  quietMs,
  signal,
  subscribe,
  timeoutMs,
}: {
  isBusy: () => boolean;
  quietMs: number;
  signal?: AbortSignal;
  subscribe: (listener: () => void) => () => void;
  timeoutMs: number;
}) {
  return new Promise<QuietResult>((resolve) => {
    if (signal?.aborted) {
      resolve('aborted');
      return;
    }

    let quietTimer: ReturnType<typeof setTimeout> | undefined;
    const finish = (result: QuietResult) => {
      clearTimeout(quietTimer);
      clearTimeout(timeoutTimer);
      unsubscribe();
      signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };
    const check = () => {
      clearTimeout(quietTimer);
      if (!isBusy()) quietTimer = setTimeout(() => finish('quiet'), quietMs);
    };
    const onAbort = () => finish('aborted');

    const unsubscribe = subscribe(check);
    const timeoutTimer = setTimeout(() => finish('timeout'), timeoutMs);
    signal?.addEventListener('abort', onAbort);
    check();
  });
}
